import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Button, EditIcon, Page } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetwork } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import RetailerNetworkUsersConfigurationSection from '../retailer-network-users-configuration';
import { RetailerNetworkLocationsAndGroups as RetailerNetworkLocationsAndGroupsSection } from '../retailer-network-details-page/components/retailer-network-locations-and-groups/retailer-network-locations-and-groups.component';
import { RetailerNetworkUserDetailsLocationsAndLocationGroups } from '../retailer-network-user-details-page/retailer-network-user-details-locations-and-groups/retailer-network-user-details-locations-and-groups.component';
import { routeCreator } from '../retailer-connect.utils';

const PAGE_TITLE = 'My Stores';
const BREADCRUMB_LINKS = [{ label: PAGE_TITLE }];

const RetailerNetworkMyStoresPage = ({
  retailerNetwork,
  retailerNetworkUpdate,
}: WithRetailerNetworkProps) => {
  useBreadcrumbLinks(BREADCRUMB_LINKS);

  const {
    isRetailerNetworkSiteUserRole,
    retailerNetworkUserUuid,
    retailerNetworkUserRole,
    retailerNetworkUserOpenCommerceUserUuid,
  } = useRetailerNetworkUserSelector();

  const retailerNetworkSiteUserContent = useMemo(() => {
    if (
      !retailerNetwork.uuid ||
      !retailerNetworkUserUuid ||
      !retailerNetworkUserRole ||
      !retailerNetworkUserOpenCommerceUserUuid
    ) {
      return null;
    }
    return (
      <RetailerNetworkUserDetailsLocationsAndLocationGroups
        retailerNetworkUuid={retailerNetwork.uuid}
        retailerNetworkUserUuid={retailerNetworkUserUuid}
        retailerNetworkUserRole={retailerNetworkUserRole}
        openCommerceUserUuid={retailerNetworkUserOpenCommerceUserUuid}
      />
    );
  }, [
    retailerNetwork.uuid,
    retailerNetworkUserUuid,
    retailerNetworkUserRole,
    retailerNetworkUserOpenCommerceUserUuid,
  ]);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        titleTestId: 'rc-my-stores-title',
        subtitle: isRetailerNetworkSiteUserRole
          ? ''
          : `You currently manage ${retailerNetwork.numberOfLocations} stores.`,
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <Can I={UpdateRetailerNetwork(retailerNetwork)}>
          <Button
            color='secondary'
            variant='contained'
            component={Link}
            startIcon={<EditIcon />}
            to={routeCreator.configurationUpdateNetworkLocations(retailerNetwork.uuid)}
            role='button'
          >
            Edit Network
          </Button>
        </Can>
      }
    >
      {isRetailerNetworkSiteUserRole ? (
        retailerNetworkSiteUserContent
      ) : (
        <>
          <RetailerNetworkLocationsAndGroupsSection
            retailerNetworkUuid={retailerNetwork.uuid}
            retailerNetworkUpdate={retailerNetworkUpdate}
            customerId={retailerNetwork.customerId}
          />
          <RetailerNetworkUsersConfigurationSection
            retailerNetwork={retailerNetwork}
            retailerNetworkUpdate={retailerNetworkUpdate}
          />
        </>
      )}
    </Page>
  );
};

export default withRetailerNetwork(RetailerNetworkMyStoresPage);
